import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CallGroup, User } from "utils/activity";

interface AllUsers {
  [userId: string]: User,
}

interface activityViewState {
  groups: CallGroup[],
  allUsers: AllUsers,
  searchUser: string,
  selectedUser: string,
}

const initialState: activityViewState = {
  groups: [],
  allUsers: {},
  searchUser: '',
  selectedUser: '',
}

interface AddOrUpdateUserPayload {
  user: User,
  loggedInUserBlock: string,
}

/**
 * Sorts an array of user IDs in place.
 * The primary sort key is `isOnline` (true comes first).
 * The secondary sort key is `name` (alphabetical).
 */
const sortUserIds = (userIds: string[], allUsers: AllUsers) => {
  userIds.sort((a, b) => {
    const userA = allUsers[a]
    const userB = allUsers[b]
    if (!userA || !userB) return 0
    if (userA.isOnline !== userB.isOnline) {
      return userA.isOnline ? -1 : 1
    }
    return userA.name.localeCompare(userB.name)
  })
}

export const activityViewSlice = createSlice({
  name: "activityView",
  initialState,
  reducers: {
    setAndGroupUsers: (state, action: PayloadAction<User[]>) => {
      // Clear existing users and groups to prevent duplication on view reload
      state.allUsers = {}
      state.groups = []

      action.payload.forEach(user => {
        let group = state.groups.find(group => group.id === user.teamId)
        if (!group) {
          group = {
            id: user.teamId,
            name: user.teamName,
            userIds: []
          }
          state.groups.push(group)
        }

        if (group) {
            // Prevent duplicates within the same payload
            if (!group.userIds.includes(user.id)) {
                group.userIds.push(user.id)
            }
        }
        state.allUsers[user.id] = user
      })

      state.groups.forEach(group => {
        sortUserIds(group.userIds, state.allUsers)
      })
    },
    addOrUpdateUsers: (state, action: PayloadAction<User[]>) => {
      action.payload.forEach(user => {
        let group = state.groups.find(group => group.id === user.teamId)
        if (!group) {
          group = {
            id: user.teamId,
            name: user.teamName,
            userIds: []
          }
          state.groups.push(group)
        }

        if (group) {
          if (!group.userIds.includes(user.id)) {
            group.userIds.push(user.id)
          }
        }
        state.allUsers[user.id] = user
      })

      state.groups.forEach(group => {
        sortUserIds(group.userIds, state.allUsers)
      })
    },
    addOrUpdateUser: (state, action: PayloadAction<AddOrUpdateUserPayload>) => {
      const { user, loggedInUserBlock } = action.payload

      // Update user data first so it's available for sorting.
      state.allUsers[user.id] = user

      state.groups.forEach(group => {
        group.userIds = group.userIds.filter(e => e !== user.id)
      })

      state.groups = state.groups.filter(e => !!e.userIds.length)

      const group = state.groups.find(group => group.id === user.teamId)

      if (group) {
        group.userIds.push(user.id)
        sortUserIds(group.userIds, state.allUsers)
      } else {
        const newBlock = user.userBlock || user.teamBlock
        if (loggedInUserBlock === '0' || loggedInUserBlock === newBlock) {
          const newGroup = {
            id: user.teamId,
            name: user.teamName,
            userIds: [user.id]
          }
          state.groups.push(newGroup)
        }
      }
    },
    setUserOnline: (state, action: PayloadAction<string>) => {
      const userId = action.payload
      const user = state.allUsers[userId]
      if (user) {
        state.allUsers[userId].isOnline = true
        const group = state.groups.find(g => g.id === user.teamId)
        if (group) {
          // This reducer mutates state via Immer, so we sort the array in place.
          sortUserIds(group.userIds, state.allUsers)
        }
      }
    },
    setUserOffline: (state, action: PayloadAction<string>) => {
      const userId = action.payload
      const user = state.allUsers[userId]
      if (user) {
          state.allUsers[userId].isOnline = false
          const group = state.groups.find(g => g.id === user.teamId)
          if (group) {
              // Re-sort to move the user down the list
              sortUserIds(group.userIds, state.allUsers)
          }
      }
    },
    setSearchUser: (state, action: PayloadAction<string>) => {
      state.searchUser = action.payload
    },
    setSelectedUser: (state, action: PayloadAction<string>) => {
      state.selectedUser = action.payload
    },
  },
});

export const { setAndGroupUsers, addOrUpdateUsers, addOrUpdateUser, setUserOnline, setUserOffline, setSearchUser, setSelectedUser } = activityViewSlice.actions;

export default activityViewSlice.reducer;
